import http from "@/api/http.js";

/**
 * Push Notification Service
 * Handles browser push notification subscription and management
 */

// Convert base64 URL to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported in the browser
 */
export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window;
}

/**
 * Get current notification permission status
 * @returns {string} 'granted', 'denied', or 'default'
 */
export function getNotificationPermission() {
  if (!("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

/**
 * Request notification permission from the user
 * @returns {Promise<string>} Permission status
 */
export async function requestNotificationPermission() {
  if (!("Notification" in window)) {
    throw new Error("Notifications not supported");
  }
  return await Notification.requestPermission();
}

/**
 * Get the VAPID public key from the backend
 * @returns {Promise<string>}
 */
export async function getVapidPublicKey() {
  const response = await http.get("/push/vapid_key");
  return response.data.publicKey;
}

/**
 * Get service worker registration with timeout
 * @returns {Promise<ServiceWorkerRegistration>}
 */
async function getServiceWorkerRegistration() {
  if (!navigator.serviceWorker) {
    throw new Error("Service Worker not supported");
  }

  // Check if there's already a registration
  const existingReg = await navigator.serviceWorker.getRegistration();
  if (existingReg) {
    console.log("Found existing service worker registration");
    return existingReg;
  }

  // Wait for service worker to be ready with a timeout
  const timeout = 10000; // 10 seconds
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(
      () =>
        reject(
          new Error(
            "Service Worker registration timeout. Please refresh the page."
          )
        ),
      timeout
    );
  });

  return Promise.race([navigator.serviceWorker.ready, timeoutPromise]);
}

/**
 * Subscribe the browser to push notifications
 * @param {string} departmentCode Optional department code for filtering notifications
 * @returns {Promise<object>} Subscription result
 */
export async function subscribeToPush(departmentCode = null) {
  console.log("Subscribing to push notifications...");
  if (!isPushSupported()) {
    throw new Error("Push notifications not supported");
  }

  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission denied");
  }
  console.log("Notification permission granted.");

  // Get the service worker registration with timeout
  const registration = await getServiceWorkerRegistration();
  console.log("Service Worker ready:", registration);

  // Always unsubscribe first to get a fresh subscription
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    console.log("Removing existing subscription to create fresh one...");
    await existingSubscription.unsubscribe();
  }

  // Get VAPID public key from backend and create new subscription
  const vapidPublicKey = await getVapidPublicKey();
  console.log("VAPID Public Key:", vapidPublicKey);
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  console.log("New push subscription created:", subscription);
  // Send subscription to backend
  const subscriptionData = subscription.toJSON();
  const response = await http.post("/push/subscribe", {
    endpoint: subscriptionData.endpoint,
    keys: {
      p256dh: subscriptionData.keys.p256dh,
      auth: subscriptionData.keys.auth
    },
    departmentCode: departmentCode
  });
  console.log("Subscription sent to backend:", response.data);
  return response.data;
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<object>}
 */
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    // Notify backend
    await http.post("/push/unsubscribe", {
      endpoint: subscription.endpoint
    });

    // Unsubscribe locally
    await subscription.unsubscribe();
  }

  return { success: true };
}

/**
 * Check if currently subscribed to push notifications
 * @returns {Promise<boolean>}
 */
export async function isSubscribedToPush() {
  if (!isPushSupported()) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null;
}

/**
 * Get current push subscription
 * @returns {Promise<PushSubscription|null>}
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  return await registration.pushManager.getSubscription();
}

/**
 * Send a test notification (for admin testing)
 * @returns {Promise<object>}
 */
export async function sendTestNotification() {
  const response = await http.post("/push/test");
  return response.data;
}

/**
 * Initialize push notification listener for in-app handling
 * Call this in your main Vue component
 * @param {function} callback Function to call when notification is clicked
 */
export function initPushListener(callback) {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", event => {
      if (event.data && event.data.type === "NOTIFICATION_CLICKED") {
        callback(event.data.data);
      }
      // Play notification sound when requested by service worker
      if (event.data && event.data.type === "PLAY_NOTIFICATION_SOUND") {
        playNotificationSound();
      }
    });
  }
}

/**
 * Play notification sound
 */
function playNotificationSound() {
  try {
    const audio = new Audio("/sounds/notification-2.mp3");
    audio.volume = 0.5;
    audio
      .play()
      .catch(e => console.log("Could not play notification sound:", e));
  } catch (e) {
    console.log("Audio not supported:", e);
  }
}

export default {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  getCurrentSubscription,
  sendTestNotification,
  initPushListener
};
